import { useMemo } from 'react'
import { Search, X } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '../ui/select'
import {
  CLASSIC_TEMPLATES,
  DESIGNED_TEMPLATES,
  PAINTED_TEMPLATES,
  TEMPLATE_CATEGORIES,
  filterTemplates,
} from '../../lib/inviteTemplates'
import type { TemplateCategory, TemplateCollection } from '../../lib/inviteTemplates'
import { PAINTED_LOOKS } from '../../lib/paintedInvite'
import type { PaintedLook } from '../../lib/paintedInvite'

/**
 * The template library's one filter row: a search box, a Style menu (all ·
 * painted with its four looks · classic, each only while the library lists it)
 * and a Category menu whose counts follow the style. The Design step's picker
 * and the public gallery share it.
 */

export type TemplateStyle = 'all' | TemplateCollection | PaintedLook

export const styleFilter = (style: TemplateStyle): { collection: TemplateCollection | 'all'; look: PaintedLook | 'all' } =>
  style === 'all' || style === 'painted' || style === 'classic'
    ? { collection: style, look: 'all' }
    : { collection: 'painted', look: style }

type Props = {
  query: string
  onQuery: (v: string) => void
  style: TemplateStyle
  onStyle: (v: TemplateStyle) => void
  category: TemplateCategory | 'all'
  onCategory: (v: TemplateCategory | 'all') => void
  appLang?: string
  size?: 'sm' | 'md'
  testPrefix: string
}

const TemplateFilterBar = ({ query, onQuery, style, onStyle, category, onCategory, appLang = 'en', size = 'sm', testPrefix }: Props) => {
  const ka = appLang === 'ka'
  const t = (en: string, ka_: string) => (ka ? ka_ : en)
  const md = size === 'md'

  const lookCounts = useMemo(
    () => Object.fromEntries(PAINTED_LOOKS.map((l) => [l.id, PAINTED_TEMPLATES.filter((tpl) => tpl.painted?.look === l.id).length])),
    [],
  )
  const categoryCounts = useMemo(() => {
    const pool = filterTemplates(styleFilter(style)).filter((tpl) => tpl.id !== 'auto')
    return Object.fromEntries(TEMPLATE_CATEGORIES.map((c) => [c.id, pool.filter((tpl) => tpl.category === c.id).length]))
  }, [style])

  const field = md
    ? 'h-11 rounded-full border-[#1b1033]/12 bg-white text-sm shadow-celebrate-sm dark:border-white/10 dark:bg-white/5'
    : 'h-9 rounded-full border-[#1b1033]/12 bg-white text-xs dark:border-white/10 dark:bg-white/5'
  const trigger = `${field} px-3.5 text-[#1b1033] dark:text-[#f4f0ff] focus:ring-[#ff4d8d]/40`
  const count = 'ml-1.5 tabular-nums opacity-60'

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testPrefix}-filters`}>
      <label className="relative block min-w-0 flex-[1_1_100%] sm:flex-[1_1_9rem]">
        <Search
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-[#4a3d66]/70 dark:text-[#c3b8dd]/70 ${md ? 'left-4 h-4 w-4' : 'left-3 h-3.5 w-3.5'}`}
        />
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t('Search — dinosaur, wedding, office…', 'ძებნა — დინოზავრი, ქორწილი, ოფისი…')}
          aria-label={t('Search templates', 'შაბლონის ძებნა')}
          className={`w-full border text-[#1b1033] outline-none placeholder:text-[#4a3d66]/60 focus:border-[#ff4d8d] dark:text-[#f4f0ff] dark:placeholder:text-[#c3b8dd]/60 ${field} ${md ? 'pl-11 pr-10' : 'pl-9 pr-9'}`}
          data-testid={`${testPrefix}-search`}
        />
        {query && (
          <button
            type="button"
            onClick={() => onQuery('')}
            aria-label={t('Clear search', 'ძებნის გასუფთავება')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#4a3d66] hover:text-[#ff4d8d] dark:text-[#c3b8dd]"
          >
            <X className={md ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
          </button>
        )}
      </label>

      <div className="min-w-0 flex-[1_1_8rem] sm:flex-[0_1_9rem]">
        <Select
          value={style}
          onValueChange={(v) => {
            const next = v as TemplateStyle
            onStyle(next)
            // Otherwise the host keeps a category the new style has none of,
            // and the grid just goes empty.
            if (category !== 'all' && !filterTemplates({ category, ...styleFilter(next) }).some((tpl) => tpl.id !== 'auto')) onCategory('all')
          }}
        >
          <SelectTrigger className={trigger} aria-label={t('Style', 'სტილი')} data-testid={`${testPrefix}-style`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid={`${testPrefix}-style-all`}>
              {t('All styles', 'ყველა სტილი')}
              <span className={count}>{DESIGNED_TEMPLATES.length}</span>
            </SelectItem>
            <SelectSeparator />
            {PAINTED_TEMPLATES.length > 0 && (
              <>
                <SelectItem value="painted" className="font-semibold" data-testid={`${testPrefix}-style-painted`}>
                  {t('Painted', 'ნახატი')}
                  <span className={count}>{PAINTED_TEMPLATES.length}</span>
                </SelectItem>
                {PAINTED_LOOKS.map((l) => (
                  <SelectItem key={l.id} value={l.id} className="pl-5" data-testid={`${testPrefix}-style-${l.id}`}>
                    {ka ? l.label.ka : l.label.en}
                    <span className={count}>{lookCounts[l.id]}</span>
                  </SelectItem>
                ))}
              </>
            )}
            {CLASSIC_TEMPLATES.length > 0 && (
              <>
                {PAINTED_TEMPLATES.length > 0 && <SelectSeparator />}
                <SelectItem value="classic" className="font-semibold" data-testid={`${testPrefix}-style-classic`}>
                  {t('Classic', 'კლასიკური')}
                  <span className={count}>{CLASSIC_TEMPLATES.length}</span>
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="min-w-0 flex-[1_1_9rem] sm:flex-[0_1_10rem]">
        <Select value={category} onValueChange={(v) => onCategory(v as TemplateCategory | 'all')}>
          <SelectTrigger className={trigger} aria-label={t('Category', 'კატეგორია')} data-testid={`${testPrefix}-category`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid={`${testPrefix}-category-all`}>
              {t('Every occasion', 'ყველა კატეგორია')}
            </SelectItem>
            <SelectSeparator />
            {TEMPLATE_CATEGORIES.map((c) => (
              <SelectItem
                key={c.id}
                value={c.id}
                disabled={!categoryCounts[c.id] && category !== c.id}
                data-testid={`${testPrefix}-category-${c.id}`}
              >
                {ka ? c.label.ka : c.label.en}
                <span className={count}>{categoryCounts[c.id]}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

export default TemplateFilterBar
